import { DEFAULT_DATE_FORMAT, type DateFormat } from "@/features/profile-setup/_logic/constants";

/**
 * The Intl locale each date format renders with. The only place a format maps
 * to output: every call site passes the locale to Intl and lets it order the
 * parts, so "Oct 20, 2026" becomes "20 Oct 2026" without per-site branching.
 */
export const DATE_FORMAT_LOCALES = {
  "MM/DD/YYYY": "en-US",
  "DD/MM/YYYY": "en-GB",
  "YYYY-MM-DD": "en-CA",
} as const satisfies Record<DateFormat, string>;

/** An Intl locale produced by a {@link DateFormat}. */
export type DateLocale = (typeof DATE_FORMAT_LOCALES)[DateFormat];

/** Locale of {@link DEFAULT_DATE_FORMAT}, the fallback for helpers called without one. */
export const DEFAULT_DATE_LOCALE: DateLocale = DATE_FORMAT_LOCALES[DEFAULT_DATE_FORMAT];

/** Default Intl options: "Oct 20, 2026" (en-US, en-CA) or "20 Oct 2026" (en-GB). */
export const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

/** All-numeric options: "10/20/2026", "20/10/2026", or "2026-10-20". */
export const NUMERIC_DATE_OPTIONS: Intl.DateTimeFormatOptions = { month: "numeric", day: "numeric", year: "numeric" };

/** Weekday and day without year: "Tue, Oct 20" (en-US, en-CA) or "Tue 20 Oct" (en-GB). */
export const WEEKDAY_DATE_OPTIONS: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" };

/** A date as call sites hold it: a `Date`, epoch millis, or an ISO string. */
export type DateInput = Date | string | number;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Converts a {@link DateInput} to a `Date`. A bare `YYYY-MM-DD` string is read
 * as local midnight, the way dayjs and moment read it, so a calendar date never
 * shifts a day in timezones west of UTC.
 *
 * @param value - Date, epoch millis, or ISO string.
 * @returns The corresponding `Date` (invalid when the input does not parse).
 */
export function toDate(value: DateInput): Date {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  const dateOnly = DATE_ONLY.exec(value);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  return new Date(value);
}

/**
 * Formats a date in a locale, the way `Date#toLocaleDateString` does,
 * including "Invalid Date" for input that does not parse.
 *
 * @param locale - Locale from {@link DATE_FORMAT_LOCALES}.
 * @param value - Date, epoch millis, or ISO string.
 * @param options - Intl options; defaults to {@link DEFAULT_DATE_OPTIONS}.
 * @returns The formatted date.
 */
export function formatDate(
  locale: DateLocale,
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_OPTIONS
): string {
  return toDate(value).toLocaleDateString(locale, options);
}
